import React, { useState, useEffect, useRef, useMemo } from "react";
import Map, { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import Supercluster from "supercluster";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useSiteContext } from "../Providers/SSCProvider";
import { useMapContext } from "../Providers/MapProvider";
import { useRoleContext } from "../Providers/RoleProvider";

const MAPBOX_TOKEN =
  "pk.eyJ1Ijoic3RlZmFuLW0iLCJhIjoiY21mbDVwMWljMDFsZzJsczU4NGFzYXc1eSJ9.Pqi9hlSGD_ZMueCYlji_RA";

function SearchMap() {
  const { globalSitesData, filteredSites, handleSelectSite } = useSiteContext();
  const { getSiteBreakdown } = useMapContext();
  const highlightedSiteIds = filteredSites.map((site) => site.siteId);
  const highlightedSet = new Set(highlightedSiteIds);
  const mapRef = useRef();
  const [viewState, setViewState] = useState({
    latitude: 22.5,
    longitude: 12.5,
    zoom: 1.58,
    bearing: 0,
    pitch: 0,
  });
  const [clusters, setClusters] = useState([]);
  const superclusterRef = useRef(null);
  const [bounds, setBounds] = useState([-180, -85, 180, 85]);
  const { isAdmin } = useRoleContext();

  // Memoize points so the array reference only changes when sites changes
  const points = useMemo(
    () =>
      globalSitesData
        .filter(
          (site) =>
            site.lat &&
            site.lon &&
            !isNaN(Number(site.lat)) &&
            !isNaN(Number(site.lon)) &&
            Array.isArray(site.structures) &&
            site.structures.some(
              (structure) =>
                Array.isArray(structure.circuits) &&
                structure.circuits.some(
                  (circuit) =>
                    circuit.status &&
                    (circuit.status.toLowerCase() === "active" ||
                      circuit.status.toLowerCase() === "wip"),
                ),
            ),
        )
        .map((site) => {
          const hasActiveCircuits = site.structures?.some(
            (structure) =>
              Array.isArray(structure.circuits) &&
              structure.circuits.some(
                (circuit) => circuit.status?.toLowerCase() === "active",
              ),
          );
          const hasWipCircuits =
            !hasActiveCircuits &&
            site.structures?.some(
              (structure) =>
                Array.isArray(structure.circuits) &&
                structure.circuits.some(
                  (circuit) => circuit.status?.toLowerCase() === "wip",
                ),
            );
          return {
            type: "Feature",
            properties: {
              cluster: false,
              siteId: site.siteId,
              siteName: site.siteName,
              hasActiveCircuits,
              hasWipCircuits,
            },
            geometry: {
              type: "Point",
              coordinates: [parseFloat(site.lon), parseFloat(site.lat)],
            },
          };
        }),
    [globalSitesData],
  );

  // Create or update supercluster instance when points change
  useEffect(() => {
    if (points.length === 0) return;
    superclusterRef.current = new Supercluster({
      radius: 40,
      maxZoom: 16,
    });
    superclusterRef.current.load(points);
  }, [points]);

  // Update clusters when viewState or points change
  useEffect(() => {
    if (!superclusterRef.current) return;
    const clusters = superclusterRef.current.getClusters(
      bounds,
      Math.round(viewState.zoom),
    );
    setClusters(clusters);
  }, [viewState, points, bounds]);

  return (
    <>
      <style>
        {`
                .mapboxgl-ctrl {
                    display: none !important;
                }
                `}
      </style>
      <div
        style={{
          width: "90vw",
          height: "70vh",
          border: "1.5px solid #9ca3af",
          // borderRadius: "5px",
          overflow: "hidden",
          boxShadow: "0 0px 10px #0000004d",
          margin: "0 auto",
        }}
      >
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(evt) => {
            setViewState(evt.viewState);
            const b = evt.target.getBounds();
            setBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/light-v9"
          mapboxAccessToken={MAPBOX_TOKEN}
          projection={"mercator"}
          minZoom={1.58}
        >
          <NavigationControl position="top-left" />
          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            if (cluster.properties.cluster) {
              const clusterLeaves = superclusterRef.current.getLeaves(
                cluster.id,
                Infinity,
              );

              const nonHighlightedLeaves = clusterLeaves.filter(
                (leaf) => !highlightedSet.has(leaf.properties.siteId),
              );
              if (nonHighlightedLeaves.length === 0) return null;

              const greenCount = nonHighlightedLeaves.filter(
                (leaf) => leaf.properties.hasActiveCircuits,
              ).length;

              const magentaCount = nonHighlightedLeaves.filter(
                (leaf) => leaf.properties.hasWipCircuits,
              ).length;

              const totalCount = greenCount + magentaCount;

              return (
                <Marker
                  key={`cluster-${cluster.id}`}
                  longitude={longitude}
                  latitude={latitude}
                  anchor="bottom"
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-${cluster.properties.siteId}`}>
                        <div>
                          <div>
                            {greenCount > 0 && (
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginRight: magentaCount > 0 ? "12px" : "0",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "14px",
                                    height: "14px",
                                    borderRadius: "50%",
                                    background: "#09c76eff",
                                    border: "1px solid #09c76eff",
                                  }}
                                ></span>
                                <span>{greenCount}</span>
                              </div>
                            )}
                            {magentaCount > 0 && (
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "14px",
                                    height: "14px",
                                    borderRadius: "50%",
                                    background: "#8B008B",
                                    border: "1px solid #8B008B",
                                  }}
                                ></span>
                                <span>{magentaCount}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Tooltip>
                    }
                  >
                    <div
                      style={{ position: "relative", width: 38, height: 38 }}
                    >
                      {/* SVG Pie Border */}
                      <svg
                        width={38}
                        height={38}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          pointerEvents: "none",
                        }}
                      >
                        {totalCount > 0 && (
                          <>
                            {/* Green segment */}
                            <circle
                              r="15"
                              cx="19"
                              cy="19"
                              fill="transparent"
                              stroke="#09c26cff"
                              strokeWidth="9"
                              strokeDasharray={`${
                                (greenCount / totalCount) * 2 * Math.PI * 15
                              } ${
                                (magentaCount / totalCount) * 2 * Math.PI * 15
                              }`}
                              strokeDashoffset="0"
                              style={{
                                transform: "rotate(-90deg)",
                                transformOrigin: "center",
                              }}
                            />
                            {/* Magenta segment */}
                            <circle
                              r="15"
                              cx="19"
                              cy="19"
                              fill="transparent"
                              stroke="#8B008B"
                              strokeWidth="9"
                              strokeDasharray={`${
                                (magentaCount / totalCount) * 2 * Math.PI * 15
                              } ${
                                (greenCount / totalCount) * 2 * Math.PI * 15
                              }`}
                              strokeDashoffset="0"
                              style={{
                                transform: `rotate(${
                                  (greenCount / totalCount) * 360 - 90
                                }deg)`,
                                transformOrigin: "center",
                              }}
                            />
                          </>
                        )}
                      </svg>
                      {/* Cluster pin */}
                      <div
                        style={{
                          background: "#ffffffff",
                          borderRadius: "50%",
                          width: 30,
                          height: 30,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#242323ff",
                          fontWeight: "bold",
                          fontSize: 14,
                          boxShadow: "0 0 4px #333",
                          cursor: "pointer",
                          position: "absolute",
                          top: 4,
                          left: 4,
                        }}
                        onClick={() => {
                          if (superclusterRef.current && cluster.id) {
                            const expansionZoom =
                              superclusterRef.current.getClusterExpansionZoom(
                                cluster.id,
                              );
                            mapRef.current?.flyTo({
                              center: [longitude, latitude],
                              zoom: expansionZoom,
                              duration: 2000,
                            });
                          }
                        }}
                      >
                        {nonHighlightedLeaves.length}
                      </div>
                    </div>
                  </OverlayTrigger>
                </Marker>
              );
            }

            // Single site
            const isHighlighted = highlightedSet.has(cluster.properties.siteId);
            const point = points.find(
              (p) => p.properties.siteId === cluster.properties.siteId,
            );
            const hasActiveCircuits = point?.properties.hasActiveCircuits;
            const hasWipCircuits = point?.properties.hasWipCircuits;
            const site = globalSitesData.find(
              (s) => s.siteId === cluster.properties.siteId,
            );
            const breakdown = site
              ? getSiteBreakdown(site)
              : { bandwidth: "N/A", cost: "N/A" };
            let markerColor = "#1976d2"; // default blue
            // get sites with active circuits only

            if (hasActiveCircuits) {
              markerColor = "#09c76eff"; // green
            } else if (hasWipCircuits) {
              markerColor = "#8B008B"; // magenta
            }

            return (
              <Marker
                key={`site-${cluster.properties.siteId}`}
                longitude={longitude}
                latitude={latitude}
                anchor="bottom"
              >
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-${cluster.properties.siteId}`}>
                      <div>
                        <div>
                          <strong>
                            {cluster.properties.siteName} -{" "}
                            {cluster.properties.siteId}
                          </strong>
                        </div>
                        {hasActiveCircuits && (
                          <div>
                            Bandwidth: {breakdown.bandwidth}
                            {isAdmin && <div>Cost: ${breakdown.cost}</div>}
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  }
                >
                  <div
                    style={{
                      background: markerColor,
                      borderRadius: "50%",
                      width: isHighlighted ? 44 : 30,
                      height: isHighlighted ? 44 : 30,
                      boxShadow: "0 0 2px #333",
                      position: "relative",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: 14,
                    }}
                    onClick={() => {
                      if (handleSelectSite) {
                        handleSelectSite(cluster.properties.siteId, null);
                      }
                    }}
                  >
                    1
                  </div>
                </OverlayTrigger>
              </Marker>
            );
          })}
          {highlightedSiteIds
            .filter((siteId) =>
              points.some((p) => p.properties.siteId === siteId),
            )
            .map((siteId) => {
              const point = points.find((p) => p.properties.siteId === siteId);
              const hasActiveCircuits = point?.properties.hasActiveCircuits;
              const hasWipCircuits = point?.properties.hasWipCircuits;
              let markerColor = "#1976d2";
              if (hasActiveCircuits) {
                markerColor = "#09c76eff"; // green
              } else if (hasWipCircuits) {
                markerColor = "#8B008B"; // magenta for WIP
              }

              // You can also get the breakdown from the original site object if needed:
              const site = globalSitesData.find((s) => s.siteId === siteId);
              const breakdown = site
                ? getSiteBreakdown(site)
                : { bandwidth: "N/A", cost: "N/A" };

              return (
                <Marker
                  key={`highlighted-site-${siteId}`}
                  longitude={point.geometry.coordinates[0]}
                  latitude={point.geometry.coordinates[1]}
                  anchor="bottom"
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-${siteId}`}>
                        <div>
                          <strong>
                            {point.properties.siteName} - {siteId}
                          </strong>
                          <div>Bandwidth: {breakdown.bandwidth}</div>
                          {isAdmin && <div>Cost: ${breakdown.cost}</div>}
                        </div>
                      </Tooltip>
                    }
                  >
                    <div
                      style={{
                        background: markerColor,
                        borderRadius: "50%",
                        width: 44,
                        height: 44,
                        boxShadow: "0 0 2px #333",
                        position: "relative",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: 18,
                        zIndex: 2,
                        transition: "all 0.2s",
                      }}
                      onClick={() => {
                        if (handleSelectSite) {
                          handleSelectSite(siteId, null);
                        }
                      }}
                    >
                      1
                    </div>
                  </OverlayTrigger>
                </Marker>
              );
            })}
        </Map>
      </div>
    </>
  );
}

export default SearchMap;
